import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, Send, Trash2 } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { cn } from '@/utils/cn';
import type { PlanVisitGroup as PlanVisitGroupType } from '@/hooks/useTreatmentPlans';
import { useReorderPlanVisit, useUpdatePlanStatus } from '@/hooks/useTreatmentPlans';
import type { EnrichedPlanItem, PlanStatus } from '@/hooks/useTreatmentPlans';
import { PlanItemRow, PlanItemRowHeader } from './PlanItemRow';
import toast from 'react-hot-toast';

// ── Status badge ───────────────────────────────────────────────────────────────

const STATUS_BADGE: Record<PlanStatus, string> = {
  proposed: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400',
  accepted: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400',
  scheduled: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-400',
  completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400',
  cancelled: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400',
};

// ── Props ─────────────────────────────────────────────────────────────────────

interface PlanVisitGroupProps {
  group: PlanVisitGroupType;
  patientId: string;
  clinicId: string;
  allVisitNumbers: number[];
  onEditRequest: (item: EnrichedPlanItem) => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function PlanVisitGroup({
  group,
  patientId,
  clinicId,
  onEditRequest,
}: PlanVisitGroupProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(true);

  const reorder = useReorderPlanVisit();
  const updateStatus = useUpdatePlanStatus();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    // Determine target visit from over.id (could be item id or group marker)
    // For inter-group drag, user drops onto the group header element
    // We encode target visit as "group-{visit_number}" in the droppable id
    const overId = String(over.id);
    if (overId.startsWith('group-')) {
      const targetVisit = parseInt(overId.replace('group-', ''), 10);
      if (!isNaN(targetVisit)) {
        try {
          await reorder.mutateAsync({
            patientId,
            planId: String(active.id),
            newVisitNumber: targetVisit,
          });
        } catch {
          toast.error(t('common.error'));
        }
      }
    }
  };

  const handleSendQuote = () => {
    // TODO: Parse plan items in quotes module NewQuote when TAND-quote integration lands
    navigate(`/quotes/new?patientId=${patientId}&fromPlanVisit=${group.visit_number}`);
  };

  const handleDeleteVisit = async () => {
    if (
      !confirm(
        t('dental.plan.actions.confirmDeleteVisit', {
          defaultValue: `Vil du slette hele Besøg ${group.visit_number}? Alle elementer annulleres.`,
          n: group.visit_number,
        }),
      )
    )
      return;

    try {
      await Promise.all(
        group.items.map((item) =>
          updateStatus.mutateAsync({
            patientId,
            planId: item.id,
            status: 'cancelled',
          }),
        ),
      );
      toast.success(t('dental.plan.actions.deleteVisit'));
    } catch {
      toast.error(t('common.error'));
    }
  };

  const statusKey = group.status as PlanStatus;

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
      {/* Group header */}
      <div
        className={cn(
          'flex items-center gap-2 px-4 py-3 bg-slate-50 dark:bg-slate-800/50',
          'border-b border-slate-200 dark:border-slate-700',
        )}
      >
        <button
          type="button"
          onClick={() => setIsOpen((v) => !v)}
          className="flex items-center gap-2 flex-1 min-w-0 text-left"
        >
          {isOpen ? (
            <ChevronDown className="h-4 w-4 text-slate-400 flex-shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
          )}
          <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
            {t('dental.plan.visit', { n: group.visit_number })}
          </span>
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-xs font-medium ml-1',
              STATUS_BADGE[statusKey] ?? STATUS_BADGE['proposed'],
            )}
          >
            {t(`dental.plan.status.${statusKey}`, { defaultValue: statusKey })}
          </span>
          {group.items.some((i) => i.planned_date) && (
            <span className="rounded-full bg-slate-100 dark:bg-slate-700 px-2 py-0.5 text-xs text-slate-500 dark:text-slate-400">
              {group.items.find((i) => i.planned_date)?.planned_date}
            </span>
          )}
        </button>

        {/* Subtotal */}
        <span className="text-sm font-semibold text-slate-700 dark:text-slate-200 mr-2">
          {group.subtotal > 0 ? `${group.subtotal.toLocaleString('da-DK')} kr` : ''}
        </span>

        {/* Actions */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={handleSendQuote}
            className="flex items-center gap-1 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2.5 py-1 text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            title={t('dental.plan.actions.sendQuote')}
          >
            <Send className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{t('dental.plan.actions.sendQuote')}</span>
          </button>
          <button
            type="button"
            onClick={handleDeleteVisit}
            className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            title={t('dental.plan.actions.deleteVisit')}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Items */}
      {isOpen && (
        <div className="p-2 space-y-0.5 bg-white dark:bg-slate-900">
          <PlanItemRowHeader />
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={group.items.map((i) => i.id)}
              strategy={verticalListSortingStrategy}
            >
              {group.items.map((item) => (
                <PlanItemRow
                  key={item.id}
                  item={item}
                  patientId={patientId}
                  clinicId={clinicId}
                  onEditRequest={onEditRequest}
                />
              ))}
            </SortableContext>
          </DndContext>

          {/* Subtotal row */}
          {group.subtotal > 0 && (
            <div className="flex items-center justify-end pt-1.5 px-3 border-t border-slate-100 dark:border-slate-800 mt-1">
              <span className="text-xs text-slate-400 dark:text-slate-500 mr-2">
                Subtotal besøg {group.visit_number}:
              </span>
              <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                {group.subtotal.toLocaleString('da-DK')} kr
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
